import { Badge, Code, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { Select } from 'src/design-system/Select';
import { MetadataRow } from './MetadataRow';
import { CODE_BLOCK_STYLE } from './styles';
import { DetectedEntity, ExtractionTurn } from './types';

const OUTCOME_META: Record<string, { label: string; color: string }> = {
  validated: { label: 'Validated', color: 'green' },
  validation_failed: { label: 'Validation failed', color: 'red' },
  unknown_fields: { label: 'Unknown fields', color: 'orange' },
  sanity_rejected: { label: 'Sanity rejected', color: 'orange' },
  no_tool_call: { label: 'No tool call', color: 'gray' },
  invalid_arguments: { label: 'Invalid arguments', color: 'red' },
  invalid_json: { label: 'Invalid JSON', color: 'red' },
};

function outcomeMeta(outcome: string) {
  return OUTCOME_META[outcome] ?? { label: outcome, color: 'blue' };
}

function TurnCard({ turn }: { turn: ExtractionTurn }) {
  const meta = outcomeMeta(turn.outcome);
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Text fw={600} size="sm">
              Turn {turn.turn}
            </Text>
            <Badge color={meta.color} variant="light">
              {meta.label}
            </Badge>
          </Group>
          {turn.model ? (
            <Text size="xs" c="dimmed">
              {turn.model}
            </Text>
          ) : null}
        </Group>

        {turn.reasoning ? (
          <Stack gap={2}>
            <Text size="xs" fw={600} c="dimmed">
              Reasoning
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {turn.reasoning}
            </Code>
          </Stack>
        ) : null}

        {turn.submittedJson ? (
          <Stack gap={2}>
            <Text size="xs" fw={600} c="dimmed">
              Submitted input
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {turn.submittedJson}
            </Code>
          </Stack>
        ) : null}

        {turn.feedback ? (
          <Stack gap={2}>
            <Text size="xs" fw={600} c="dimmed">
              Feedback returned to model
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {turn.feedback}
            </Code>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ClassPanel({ entityType, entities }: { entityType: string; entities: DetectedEntity[] }) {
  const firstEntity = entities[0];
  const label = firstEntity.entityLabel || entityType || 'Unknown';
  const trace = firstEntity.llmExtractionTrace ?? [];

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Title order={3}>{label}</Title>
        <Text size="sm" c="dimmed">
          {entities.length} {entities.length === 1 ? 'entity' : 'entities'} extracted
        </Text>
      </Stack>

      <Stack gap="xs">
        <MetadataRow label="Entity type" value={entityType} />
        <MetadataRow label="Model" value={firstEntity.llmModelName} />
        <MetadataRow label="Finish reason" value={firstEntity.llmFinishReason} />
      </Stack>

      {trace.length > 0 ? (
        <Stack gap="xs">
          <Title order={5}>
            Extraction flow ({trace.length} {trace.length === 1 ? 'turn' : 'turns'})
          </Title>
          {trace.map((turn, index) => (
            <TurnCard key={`${turn.turn}-${index}`} turn={turn} />
          ))}
        </Stack>
      ) : null}

      <Stack gap="xs">
        <Title order={5}>Detected {label.toLowerCase()} entities (validated output)</Title>
        {entities.map((detected, index) => (
          <Stack key={detected.id} gap={4}>
            <Text size="xs" c="dimmed" fw={600}>
              {label} {index + 1} of {entities.length}
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {JSON.stringify(detected.entity.fields, null, 2)}
            </Code>
          </Stack>
        ))}
      </Stack>

      {trace.length === 0 ? (
        <Stack gap="xs">
          <Title order={5}>Reasoning for {label.toLowerCase()}</Title>
          {firstEntity.llmReasoningContent ? (
            <Code block style={CODE_BLOCK_STYLE}>
              {firstEntity.llmReasoningContent}
            </Code>
          ) : (
            <Text c="dimmed" size="sm">
              No reasoning captured (non-thinking model, or detection ran before reasoning capture
              was enabled)
            </Text>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
}

function groupByEntityType(entities: DetectedEntity[]): Map<string, DetectedEntity[]> {
  const groups = new Map<string, DetectedEntity[]>();
  for (const detected of entities) {
    if (!detected) continue;
    const key = detected.entityType ?? 'Unknown';
    const existing = groups.get(key);
    if (existing) {
      existing.push(detected);
    } else {
      groups.set(key, [detected]);
    }
  }
  return groups;
}

export function EntityDetectionPanel({ entities }: { entities: DetectedEntity[] }) {
  const safeEntities = entities.filter(Boolean);
  const groups = Array.from(groupByEntityType(safeEntities).entries()).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const [selected, setSelected] = useState<string | null>(groups[0]?.[0] ?? null);

  if (groups.length === 0) {
    return <Text c="dimmed">No entities detected.</Text>;
  }

  const [activeType, activeGroup] =
    groups.find(([entityType]) => entityType === selected) ?? groups[0];

  return (
    <Stack gap="md">
      <Select
        label="Entity type"
        data={groups.map(([entityType, group]) => ({
          value: entityType,
          label: `${group[0].entityLabel || entityType} (${group.length})`,
        }))}
        value={activeType}
        onChange={value => {
          if (value) setSelected(value);
        }}
        allowDeselect={false}
        searchable
        maxDropdownHeight={320}
        style={{ maxWidth: 360 }}
      />
      <ClassPanel entityType={activeType} entities={activeGroup} />
    </Stack>
  );
}
